import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Copy,
  Check,
  Crown,
  Gavel,
  Users,
  DollarSign,
  ListChecks,
  Eye,
  UserPlus,
  UserMinus,
  CalendarClock,
  CalendarPlus,
  Download,
  ChevronDown,
  Plus,
  X,
  Link as LinkIcon,
  KeyRound,
  MessageSquareText,
  Send,
  Settings,
  ScanLine,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { startDraft, promoteParticipant, addPlayer, removePlayer } from "@/lib/api";
import { toast } from "sonner";
import EditSettingsDialog from "@/components/EditSettingsDialog";
import SeatLinkQR from "@/components/SeatLinkQR";
import RollCallView from "@/components/RollCallView";

export default function DraftLobby({ state, me }) {
  const { draft, participants, players } = state;
  const [copied, setCopied] = useState(false);
  const [starting, setStarting] = useState(false);
  const [promoting, setPromoting] = useState(null);
  const [showSmsInput, setShowSmsInput] = useState(false);
  const [smsPhone, setSmsPhone] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [rollCallOpen, setRollCallOpen] = useState(false);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIOS || isAndroid;

  const sendSeatViaSms = () => {
    if (!mySeatUrl) return;
    const cleaned = smsPhone.replace(/[^\d+]/g, "");
    const body = encodeURIComponent(
      `Your seat for "${draft.name}" (${draft.code}):\n${mySeatUrl}\n\nOpen this link on any device to drop right back in.`
    );
    // iOS expects ?& separator, Android expects ?
    const sep = isIOS ? "?&" : "?";
    const url = cleaned ? `sms:${cleaned}${sep}body=${body}` : `sms:${sep}body=${body}`;
    window.location.href = url;
    // Reset the inline form after the OS handler kicks in
    setTimeout(() => {
      setShowSmsInput(false);
      setSmsPhone("");
    }, 800);
  }; // participant id currently being toggled

  const shareUrl = `${window.location.origin}/join/${draft.code}`;
  const mySeatUrl = me ? `${window.location.origin}/draft/${draft.code}?as=${me.id}` : null;
  const isHost = me?.is_host;

  const drafters = participants.filter((p) => !p.is_spectator);
  const spectators = participants.filter((p) => p.is_spectator);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(draft.code);
      setCopied(true);
      toast.success("Code copied!");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Invite link copied!");
    } catch {
      toast.error("Copy failed");
    }
  };

  const copyMySeat = async () => {
    if (!mySeatUrl) return;
    try {
      await navigator.clipboard.writeText(mySeatUrl);
      toast.success("Your seat link is copied — save it!");
    } catch {
      toast.error("Copy failed");
    }
  };

  const copySeatFor = async (participant) => {
    const url = `${window.location.origin}/draft/${draft.code}?as=${participant.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${participant.username}'s seat link copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const togglePromote = async (target, nextRole) => {
    setPromoting(target.id);
    try {
      await promoteParticipant(draft.code, me.id, target.id, nextRole);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update role");
    } finally {
      setPromoting(null);
    }
  };

  const onStart = async () => {
    if (drafters.length < 2) {
      toast.error("Need at least 2 drafters to start (promote spectators first)");
      return;
    }
    setStarting(true);
    try {
      await startDraft(draft.code, me.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-10 grid lg:grid-cols-3 gap-6">
      {/* Left – room code */}
      <section className="lg:col-span-2 space-y-6">
        {draft.scheduled_start_at && (
          <ScheduleCountdown
            scheduledAt={draft.scheduled_start_at}
            draftName={draft.name}
            draftCode={draft.code}
            joinUrl={shareUrl}
          />
        )}

        <div className="bg-pbo-green text-pbo-cream rounded-3xl p-6 sm:p-10 relative overflow-hidden">
          <div className="absolute inset-0 pbo-dotted opacity-30" />
          <div className="relative">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-pbo-gold-soft">
                  Draft lobby
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl mt-2 leading-tight">
                  {draft.name}
                </h1>
                <p className="text-pbo-cream/70 text-sm mt-2">
                  Share the code. Anyone with the link can join — host picks who actually drafts.
                </p>
              </div>
              {isHost && (
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    onClick={() => setRollCallOpen(true)}
                    className="text-pbo-cream/85 hover:text-pbo-gold hover:bg-pbo-green-deep border border-pbo-gold/30 text-xs h-9"
                    data-testid="open-roll-call-btn"
                  >
                    <ScanLine className="w-3.5 h-3.5 mr-1.5" />
                    Roll-call
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setEditOpen(true)}
                    className="text-pbo-cream/85 hover:text-pbo-gold hover:bg-pbo-green-deep border border-pbo-gold/30 text-xs h-9"
                    data-testid="open-edit-settings-btn"
                  >
                    <Settings className="w-3.5 h-3.5 mr-1.5" />
                    Edit settings
                  </Button>
                </div>
              )}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-end gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-pbo-gold-soft mb-1">
                  Room code
                </div>
                <button
                  onClick={copyCode}
                  className="font-mono text-6xl sm:text-7xl tracking-[0.25em] text-pbo-gold hover:text-pbo-gold-soft transition-colors"
                  data-testid="lobby-room-code"
                >
                  {draft.code}
                </button>
              </div>
              <div className="flex gap-2 sm:ml-auto">
                <Button
                  variant="ghost"
                  onClick={copyCode}
                  className="text-pbo-cream hover:text-pbo-gold hover:bg-pbo-green-deep border border-pbo-gold/30"
                  data-testid="copy-code-btn"
                >
                  {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                  Copy code
                </Button>
                <Button
                  onClick={copyLink}
                  className="bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft"
                  data-testid="copy-link-btn"
                >
                  Copy invite link
                </Button>
              </div>
            </div>

            {mySeatUrl && (
              <div className="mt-6 pt-5 border-t border-pbo-gold/20 space-y-3">
                <div className="flex items-start gap-3 flex-wrap">
                  <KeyRound className="w-4 h-4 text-pbo-gold-soft mt-0.5 shrink-0" />
                  <div className="text-xs text-pbo-cream/80 leading-relaxed flex-1 min-w-[200px]">
                    <strong className="text-pbo-cream">Save your seat link</strong> — text it to yourself or bookmark it.
                    Open it from any device to drop right back into this draft as {me?.username}.
                  </div>
                  <div className="flex gap-2 shrink-0 items-center">
                    <SeatLinkQR
                      url={mySeatUrl}
                      username={me?.username}
                      inlineSize={48}
                      testId="my-seat-qr"
                      variant="dark"
                    />
                    <Button
                      variant="outline"
                      onClick={copyMySeat}
                      className="bg-transparent border-pbo-gold/40 text-pbo-cream hover:bg-pbo-green-deep hover:text-pbo-gold-soft h-9 text-xs"
                      data-testid="copy-my-seat-btn"
                    >
                      <LinkIcon className="w-3.5 h-3.5 mr-1.5" />
                      Copy
                    </Button>
                    {isMobile && (
                      <Button
                        variant="outline"
                        onClick={() => setShowSmsInput((v) => !v)}
                        className="bg-transparent border-pbo-gold/40 text-pbo-cream hover:bg-pbo-green-deep hover:text-pbo-gold-soft h-9 text-xs"
                        data-testid="text-my-seat-btn"
                      >
                        <MessageSquareText className="w-3.5 h-3.5 mr-1.5" />
                        Text it to me
                      </Button>
                    )}
                  </div>
                </div>

                {isMobile && showSmsInput && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      sendSeatViaSms();
                    }}
                    className="flex items-center gap-2 fade-in"
                    data-testid="sms-form"
                  >
                    <Input
                      type="tel"
                      inputMode="tel"
                      placeholder="Phone (optional)"
                      value={smsPhone}
                      onChange={(e) => setSmsPhone(e.target.value.slice(0, 20))}
                      className="bg-white text-pbo-ink border-pbo-gold/30 h-10 flex-1"
                      data-testid="sms-phone-input"
                      autoFocus
                    />
                    <Button
                      type="submit"
                      className="bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft h-10 shrink-0"
                      data-testid="sms-send-btn"
                    >
                      <Send className="w-4 h-4 mr-1.5" />
                      Open Messages
                    </Button>
                  </form>
                )}
                {isMobile && showSmsInput && (
                  <p className="text-[11px] text-pbo-cream/60 leading-snug">
                    This opens your phone&apos;s Messages app pre-filled with the link.
                    Leave the field blank and pick yourself from your contacts.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Rules summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <RuleStat icon={<DollarSign className="w-4 h-4" />} label="Budget" value={`$${draft.budget}`} />
          <RuleStat icon={<ListChecks className="w-4 h-4" />} label="Roster" value={draft.roster_size} />
          <RuleStat icon={<Users className="w-4 h-4" />} label="Field" value={players.length} />
          <RuleStat icon={<Gavel className="w-4 h-4" />} label="Timer" value={`${draft.nomination_seconds}s`} />
        </div>

        {/* Players list */}
        <FieldEditor
          players={players}
          isHost={isHost}
          hostId={me?.id}
          draftCode={draft.code}
        />
      </section>

      {/* Right – participants */}
      <aside className="space-y-4">
        {/* Drafters */}
        <RoleList
          title="Drafting"
          icon={<Gavel className="w-4 h-4 text-pbo-green" />}
          participants={drafters}
          me={me}
          isHost={isHost}
          actionLabel="Move to watching"
          actionIcon={<UserMinus className="w-3.5 h-3.5" />}
          onAction={(p) => togglePromote(p, "spectator")}
          actionDisabled={(p) => p.is_host}
          promotingId={promoting}
          emptyText="No drafters yet."
          testId="drafters-list"
          onCopySeat={copySeatFor}
          draftCode={draft.code}
        />

        {/* Spectators */}
        <RoleList
          title="Watching"
          icon={<Eye className="w-4 h-4 text-pbo-gold" />}
          participants={spectators}
          me={me}
          isHost={isHost}
          actionLabel="Add to draft"
          actionIcon={<UserPlus className="w-3.5 h-3.5" />}
          onAction={(p) => togglePromote(p, "drafter")}
          actionDisabled={() => false}
          promotingId={promoting}
          emptyText="No one watching."
          testId="spectators-list"
          onCopySeat={copySeatFor}
          draftCode={draft.code}
        />

        {isHost ? (
          <Button
            onClick={onStart}
            disabled={starting || drafters.length < 2}
            className="w-full h-14 rounded-full bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft font-semibold text-base"
            data-testid="start-draft-btn"
          >
            <Gavel className="w-4 h-4 mr-2" />
            {starting ? "Starting…" : `Start the draft (${drafters.length} drafters)`}
          </Button>
        ) : (
          <div className="text-center text-sm text-pbo-muted bg-white border border-pbo-border rounded-2xl p-4">
            {me?.is_spectator
              ? <>You&apos;re <strong className="text-pbo-gold">watching</strong> — the host can promote you to a drafter from this lobby.</>
              : <>Waiting for <strong className="text-pbo-green">{participants.find((p) => p.is_host)?.username}</strong> to start the draft…</>
            }
          </div>
        )}
      </aside>

      {isHost && (
        <EditSettingsDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          draft={draft}
          hostId={me.id}
        />
      )}

      {isHost && (
        <RollCallView
          open={rollCallOpen}
          onClose={() => setRollCallOpen(false)}
          title={`${draft.name} · ${draft.code}`}
          subtitle="Drafters first, then Watchers. Each card opens that person's personal rejoin link."
          participants={[...drafters, ...spectators]}
          linkBase={`${window.location.origin}/draft/${draft.code}`}
        />
      )}
    </div>
  );
}

const RoleList = ({
  title,
  icon,
  participants,
  me,
  isHost,
  actionLabel,
  actionIcon,
  onAction,
  actionDisabled,
  promotingId,
  emptyText,
  testId,
  onCopySeat,
  draftCode,
}) => (
  <div className="bg-white border border-pbo-border rounded-2xl p-5">
    <div className="flex items-center justify-between mb-3">
      <h2 className="font-serif text-pbo-green text-lg flex items-center gap-2">
        {icon}
        {title}
      </h2>
      <span className="text-xs text-pbo-muted">{participants.length}</span>
    </div>
    {participants.length === 0 ? (
      <div className="text-xs text-pbo-muted italic py-2 px-3">{emptyText}</div>
    ) : (
      <ul className="space-y-1.5" data-testid={testId}>
        {participants.map((p) => (
          <li
            key={p.id}
            className="flex items-center gap-2 py-1.5 px-3 rounded-lg bg-pbo-paper border border-pbo-border/60"
          >
            <span
              className={`w-2 h-2 rounded-full shrink-0 ${p.connected ? "bg-pbo-green" : "bg-pbo-muted/40"}`}
            />
            <span className="text-sm font-semibold text-pbo-ink truncate flex-1">
              {p.username}
            </span>
            {p.is_host && (
              <span className="text-[10px] uppercase tracking-wider text-pbo-gold flex items-center gap-1 shrink-0">
                <Crown className="w-3 h-3" /> Host
              </span>
            )}
            {me?.id === p.id && (
              <span className="text-[10px] uppercase tracking-wider text-pbo-green-soft shrink-0">
                You
              </span>
            )}
            {isHost && onCopySeat && me?.id !== p.id && (
              <>
                <SeatLinkQR
                  url={`${window.location.origin}/draft/${draftCode || ""}?as=${p.id}`}
                  username={p.username}
                  testId={`seat-qr-${p.id}`}
                  variant="light"
                />
                <button
                  type="button"
                  onClick={() => onCopySeat(p)}
                  aria-label={`Copy ${p.username}'s seat link`}
                  title={`Copy ${p.username}'s seat link`}
                  className="h-7 px-1.5 text-pbo-muted hover:text-pbo-green transition-colors rounded"
                  data-testid={`copy-seat-${p.id}`}
                >
                  <LinkIcon className="w-3.5 h-3.5" />
                </button>
              </>
            )}
            {isHost && !actionDisabled(p) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onAction(p)}
                disabled={promotingId === p.id}
                className="h-7 px-2 text-[11px] text-pbo-green hover:bg-pbo-green hover:text-pbo-cream"
                data-testid={`role-toggle-${p.id}`}
              >
                {actionIcon}
                <span className="ml-1 hidden sm:inline">{actionLabel}</span>
              </Button>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const RuleStat = ({ icon, label, value }) => (
  <div className="bg-white border border-pbo-border rounded-xl p-3 flex items-center gap-3">
    <span className="w-9 h-9 rounded-lg bg-pbo-green/8 text-pbo-green grid place-items-center">
      {icon}
    </span>
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-pbo-muted font-semibold">
        {label}
      </div>
      <div className="font-mono text-pbo-green font-semibold">{value}</div>
    </div>
  </div>
);

const FieldEditor = ({ players, isHost, hostId, draftCode }) => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const onAdd = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      await addPlayer(draftCode, hostId, n);
      setName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add golfer");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (p) => {
    setRemovingId(p.id);
    try {
      await removePlayer(draftCode, hostId, p.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove golfer");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="bg-white border border-pbo-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-serif text-pbo-green text-xl">The field</h2>
        <span className="text-xs text-pbo-muted">
          {players.length} golfers{isHost ? " · edit anytime before start" : ""}
        </span>
      </div>

      {isHost && (
        <form
          onSubmit={onAdd}
          className="flex items-center gap-2 mb-4"
          data-testid="add-golfer-form"
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 80))}
            placeholder="Add a golfer (e.g. Justin Thomas)"
            className="bg-pbo-paper border-pbo-border"
            data-testid="add-golfer-input"
          />
          <Button
            type="submit"
            disabled={busy || !name.trim()}
            className="bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep h-10 shrink-0"
            data-testid="add-golfer-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </form>
      )}

      {players.length === 0 ? (
        <div className="text-sm text-pbo-muted italic py-4 text-center">
          The field is empty — add at least one golfer to get going.
        </div>
      ) : (
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 max-h-72 overflow-y-auto scrollbar-thin pr-2"
          data-testid="field-list"
        >
          {players.map((p) => (
            <li
              key={p.id}
              className="text-sm flex items-center gap-2 py-1.5 border-b border-pbo-border/50 group"
            >
              <span className="w-1 h-1 rounded-full bg-pbo-gold shrink-0" />
              <span className="text-pbo-ink/85 truncate flex-1">{p.name}</span>
              {isHost && (
                <button
                  type="button"
                  onClick={() => onRemove(p)}
                  disabled={removingId === p.id}
                  aria-label={`Remove ${p.name}`}
                  className="opacity-0 group-hover:opacity-100 sm:opacity-50 sm:hover:opacity-100 text-pbo-muted hover:text-pbo-green transition-opacity p-1 rounded"
                  data-testid={`remove-golfer-${p.id}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ScheduleCountdown = ({ scheduledAt, draftName, draftCode, joinUrl }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const target = new Date(scheduledAt).getTime();
  const diff = target - now;
  const past = diff <= 0;
  const absMs = Math.abs(diff);
  const days = Math.floor(absMs / 86400000);
  const hours = Math.floor((absMs % 86400000) / 3600000);
  const minutes = Math.floor((absMs % 3600000) / 60000);
  const seconds = Math.floor((absMs % 60000) / 1000);

  const friendlyAt = new Date(scheduledAt).toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  const downloadIcs = () => {
    const ics = buildIcs({ scheduledAt, draftName, draftCode, joinUrl });
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(draftName || "draft").replace(/[^a-z0-9]+/gi, "_")}_${draftCode}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Calendar invite downloaded");
  };

  const openGoogleCalendar = () => {
    const url = buildGoogleCalUrl({ scheduledAt, draftName, draftCode, joinUrl });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openOutlookCalendar = () => {
    const url = buildOutlookCalUrl({ scheduledAt, draftName, draftCode, joinUrl });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="bg-white border border-pbo-border rounded-2xl p-5 flex items-center gap-5 flex-wrap"
      data-testid="schedule-countdown"
    >
      <div className="w-12 h-12 rounded-xl bg-pbo-gold/15 text-pbo-gold grid place-items-center shrink-0">
        <CalendarClock className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.22em] text-pbo-muted font-semibold">
          {past ? "Scheduled start was" : "Scheduled start"}
        </div>
        <div className="font-serif text-pbo-green text-lg leading-tight">
          {friendlyAt}
        </div>
      </div>
      <div className="text-right" data-testid="schedule-countdown-value">
        <div className="text-[10px] uppercase tracking-[0.22em] text-pbo-muted font-semibold">
          {past ? "Past start" : "Tee off in"}
        </div>
        <div className="font-mono font-semibold text-pbo-green text-xl tabular-nums">
          {days > 0 && <span>{days}d </span>}
          {(days > 0 || hours > 0) && <span>{String(hours).padStart(2, "0")}h </span>}
          <span>{String(minutes).padStart(2, "0")}m </span>
          <span>{String(seconds).padStart(2, "0")}s</span>
          {past && <span className="text-pbo-gold ml-1">ago</span>}
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="h-9 border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream text-xs"
            data-testid="add-to-calendar-btn"
          >
            <CalendarPlus className="w-3.5 h-3.5 mr-1.5" />
            Add to calendar
            <ChevronDown className="w-3 h-3 ml-1.5 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="bg-white border-pbo-border w-56"
          data-testid="calendar-menu"
        >
          <DropdownMenuItem
            onClick={openGoogleCalendar}
            className="cursor-pointer text-pbo-ink focus:bg-pbo-paper focus:text-pbo-green"
            data-testid="add-to-google-cal-btn"
          >
            <CalendarPlus className="w-4 h-4 mr-2 text-pbo-green" />
            Google Calendar
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={openOutlookCalendar}
            className="cursor-pointer text-pbo-ink focus:bg-pbo-paper focus:text-pbo-green"
            data-testid="add-to-outlook-cal-btn"
          >
            <CalendarPlus className="w-4 h-4 mr-2 text-pbo-green" />
            Outlook (web)
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={downloadIcs}
            className="cursor-pointer text-pbo-ink focus:bg-pbo-paper focus:text-pbo-green"
            data-testid="download-ics-btn"
          >
            <Download className="w-4 h-4 mr-2 text-pbo-green" />
            Apple / Other (.ics)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Format Date → "YYYYMMDDTHHMMSSZ" in UTC.
const fmtIcsUtc = (d) =>
  [
    d.getUTCFullYear(),
    String(d.getUTCMonth() + 1).padStart(2, "0"),
    String(d.getUTCDate()).padStart(2, "0"),
    "T",
    String(d.getUTCHours()).padStart(2, "0"),
    String(d.getUTCMinutes()).padStart(2, "0"),
    String(d.getUTCSeconds()).padStart(2, "0"),
    "Z",
  ].join("");

// Build a Google Calendar "render" URL that opens in a new tab pre-filled.
const buildGoogleCalUrl = ({ scheduledAt, draftName, draftCode, joinUrl }) => {
  const start = new Date(scheduledAt);
  const end = new Date(start.getTime() + 90 * 60 * 1000);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: draftName || "Palm Breezy Open Draft",
    dates: `${fmtIcsUtc(start)}/${fmtIcsUtc(end)}`,
    details:
      `The Palm Breezy Open auction draft room.\n\n` +
      `Join link: ${joinUrl}\nRoom code: ${draftCode}\n\n` +
      `BYOB — bid bold, draft well, may the best foursome win.`,
    location: "Palm Breezy Open Draft Room",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Build an Outlook Web "deeplink" URL.
const buildOutlookCalUrl = ({ scheduledAt, draftName, draftCode, joinUrl }) => {
  const start = new Date(scheduledAt);
  const end = new Date(start.getTime() + 90 * 60 * 1000);
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    startdt: start.toISOString(),
    enddt: end.toISOString(),
    subject: draftName || "Palm Breezy Open Draft",
    body:
      `The Palm Breezy Open auction draft room.\n\n` +
      `Join link: ${joinUrl}\nRoom code: ${draftCode}`,
    location: "Palm Breezy Open Draft Room",
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

// Build an iCalendar (.ics) string for the scheduled draft.
const buildIcs = ({ scheduledAt, draftName, draftCode, joinUrl }) => {
  const start = new Date(scheduledAt);
  const end = new Date(start.getTime() + 90 * 60 * 1000); // 90 min default
  const fmt = (d) =>
    [
      d.getUTCFullYear(),
      String(d.getUTCMonth() + 1).padStart(2, "0"),
      String(d.getUTCDate()).padStart(2, "0"),
      "T",
      String(d.getUTCHours()).padStart(2, "0"),
      String(d.getUTCMinutes()).padStart(2, "0"),
      String(d.getUTCSeconds()).padStart(2, "0"),
      "Z",
    ].join("");
  const escape = (s) =>
    String(s || "")
      .replace(/\\/g, "\\\\")
      .replace(/\n/g, "\\n")
      .replace(/,/g, "\\,")
      .replace(/;/g, "\\;");
  const fold = (line) => {
    // RFC 5545 says lines should be folded at 75 octets. Simple fold every 73 chars.
    const out = [];
    let s = line;
    while (s.length > 73) {
      out.push(s.slice(0, 73));
      s = " " + s.slice(73);
    }
    out.push(s);
    return out.join("\r\n");
  };
  const uid = `pbo-${draftCode}-${start.getTime()}@palmbreezyopen.com`;
  const description = `The Palm Breezy Open auction draft room.\\n\\nJoin link: ${joinUrl}\\nRoom code: ${draftCode}\\n\\nBYOB — bid bold, draft well, may the best foursome win.`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Palm Breezy Open//Auction Draft Room//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    fold(`SUMMARY:${escape(draftName || "Palm Breezy Open Draft")}`),
    fold(`DESCRIPTION:${description}`),
    fold(`URL:${escape(joinUrl)}`),
    "LOCATION:Palm Breezy Open Draft Room",
    "STATUS:CONFIRMED",
    "TRANSP:OPAQUE",
    "BEGIN:VALARM",
    "TRIGGER:-PT15M",
    "ACTION:DISPLAY",
    `DESCRIPTION:${escape(draftName || "Palm Breezy Open Draft")} starts in 15 minutes`,
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return lines.join("\r\n");
};
